import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
	Card,
	Col,
	Form,
	Row,
	Spin,
	Switch,
	Select,
	message,
	Alert,
	Divider,
	Button,
	Space,
} from "antd";
import { SaveOutlined, ReloadOutlined, UndoOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import {
	getRiderApprovalSettings,
	updateRiderApprovalSettings,
	resetRiderApprovalSettings,
} from "../../redux/riderApprovalSettingSlice";
import { getAllCorporates } from "../../redux/corporateSlice";
import { RIDER_APPROVAL_MESSAGES } from "../../Utils/Constants/messages";

const { Option } = Select;

const DEFAULT_SETTINGS = {
	riderApprovalEnabled: true,
	approvalRequired: true,
	autoApprovalEnabled: false,
	autoApprovalAction: "APPROVED",
};

const RiderApprovalSettings = () => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();

	const { riderApprovalSettings } = useSelector(
		(state) => state.riderApprovalSettings
	);
	const { corporates } = useSelector((state) => state.corporates);
	const { loading, data } = riderApprovalSettings;

	const [makingApiCall, setMakingApiCall] = useState(false);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [selectedCorporateId, setSelectedCorporateId] = useState(null);
	const [formValues, setFormValues] = useState(DEFAULT_SETTINGS);
	const [isErrorDisplayed, setErrorDisplayed] = useState(false);

	const userCorporates = useMemo(() => {
		if (!corporates?.corporatesList) return [];
		return Object.values(corporates.corporatesList);
	}, [corporates]);

	const hasNoCorporates = userCorporates.length === 0;

	const corporateHelperText = useMemo(() => {
		if (hasNoCorporates) {
			return "You must create a corporate before configuring rider approval.";
		}
		if (selectedCorporateId) {
			return "Settings will apply to riders of the selected corporate.";
		}
		return "Please select a corporate to configure its rider approval settings.";
	}, [hasNoCorporates, selectedCorporateId]);

	const fetchSettings = useCallback(
		async (corpId) => {
			if (!corpId) return;

			setMakingApiCall(true);
			dispatch(getRiderApprovalSettings(corpId))
				.unwrap()
				.then(() => {
					setMakingApiCall(false);
				})
				.catch((err) => {
					setMakingApiCall(false);
					setErrorDisplayed(true);
					if (err.message === "RIDER_APPROVAL_SETTING_NOT_FOUND") {
						message.warning(RIDER_APPROVAL_MESSAGES.FAILED_TO_LOAD);
						setFormValues(DEFAULT_SETTINGS);
						form.setFieldsValue(DEFAULT_SETTINGS);
						setHasUnsavedChanges(true);
					} else {
						message.error(RIDER_APPROVAL_MESSAGES.SERVER_ERROR);
					}
				});
		},
		[dispatch, form]
	);

	const handleFormChange = (_, allValues) => {
		const updated = { ...allValues };
		setErrorDisplayed(false);

		if (updated.approvalRequired) {
			updated.autoApprovalEnabled = false;
		} else {
			updated.autoApprovalEnabled = true;
		}

		if (updated.autoApprovalEnabled) {
			updated.approvalRequired = false;
		}

		form.setFieldsValue(updated);
		setFormValues(updated);
		setHasUnsavedChanges(true);
	};

	const handleSave = async () => {
		if (!selectedCorporateId) {
			message.warning("Please select a corporate before saving");
			return;
		}
		setErrorDisplayed(false);
		setMakingApiCall(true);
		try {
			await dispatch(
				updateRiderApprovalSettings({
					...formValues,
					corporateId: selectedCorporateId,
				})
			).unwrap();

			await fetchSettings(selectedCorporateId);

			message.success(RIDER_APPROVAL_MESSAGES.SETTINGS_UPDATED_SUCCESS);
			setHasUnsavedChanges(false);
		} catch {
			message.error(RIDER_APPROVAL_MESSAGES.FAILED_TO_UPDATE);
		} finally {
			setMakingApiCall(false);
		}
	};

	const handleReset = () => {
		if (!data) return;

		const settings = {
			riderApprovalEnabled: true,
			approvalRequired: data.approvalRequired,
			autoApprovalEnabled: data.autoApprovalEnabled,
			autoApprovalAction: data.autoApprovalAction || "APPROVED",
		};

		if (settings.approvalRequired && settings.autoApprovalEnabled) {
			settings.autoApprovalEnabled = false;
		}

		setFormValues(settings);
		form.setFieldsValue(settings);
		setHasUnsavedChanges(false);
	};

	const handleCorporateChange = (corpId) => {
		setSelectedCorporateId(corpId);
		dispatch(resetRiderApprovalSettings());
		setFormValues(DEFAULT_SETTINGS);
		form.setFieldsValue(DEFAULT_SETTINGS);
		setHasUnsavedChanges(false);

		if (corpId) {
			fetchSettings(corpId);
		}
	};

	useEffect(() => {
		dispatch(getAllCorporates());
	}, [dispatch]);

	useEffect(() => {
		if (!data) return;

		const settings = {
			riderApprovalEnabled: data.riderApprovalEnabled ?? true,
			approvalRequired: data.approvalRequired ?? true,
			autoApprovalEnabled: data.autoApprovalEnabled ?? false,
			autoApprovalAction: data.autoApprovalAction || "APPROVED",
		};

		setFormValues(settings);
		form.setFieldsValue(settings);
		setHasUnsavedChanges(false);
	}, [data, form]);

	const getApprovalFlowDescription = () => {
		if (formValues.approvalRequired) {
			return RIDER_APPROVAL_MESSAGES.MANUAL_APPROVAL_FLOW;
		}
		if (formValues.autoApprovalEnabled) {
			return RIDER_APPROVAL_MESSAGES.AUTO_APPROVAL_FLOW(formValues.autoApprovalAction);
		}
		return RIDER_APPROVAL_MESSAGES.NO_AUTO_APPROVAL_FLOW;
	};

	const getApprovalFlowType = () => {
		if (formValues.approvalRequired) return 'info';
		if (formValues.autoApprovalEnabled && formValues.autoApprovalAction === 'APPROVED')
			return 'success';
		if (formValues.autoApprovalEnabled && formValues.autoApprovalAction === 'DENIED')
			return 'warning';
		return 'info';
	};

	return (
		<Spin spinning={loading || makingApiCall}>
			<Card
				title={RIDER_APPROVAL_MESSAGES.CARD_TITLE}
				bordered={false}
				style={{ marginBottom: 16 }}
				extra={
					<Space>
						<Button
							type="default"
							icon={<ReloadOutlined />}
							onClick={() => fetchSettings(selectedCorporateId)}
							disabled={!selectedCorporateId || hasNoCorporates}
						>
							{RIDER_APPROVAL_MESSAGES.REFRESH}
						</Button>
						<Button
							type="default"
							icon={<UndoOutlined />}
							onClick={handleReset}
							disabled={!hasUnsavedChanges || !selectedCorporateId || hasNoCorporates}
						>
							{RIDER_APPROVAL_MESSAGES.RESET}
						</Button>
						<Button
							type="primary"
							icon={<SaveOutlined />}
							onClick={handleSave}
							disabled={
								!hasUnsavedChanges || !selectedCorporateId || hasNoCorporates
							}
						>
							{RIDER_APPROVAL_MESSAGES.SAVE_CHANGES}
						</Button>
					</Space>
				}
			>
				{isErrorDisplayed && (
					<Alert
						message={RIDER_APPROVAL_MESSAGES.ERROR_TITLE}
						description={RIDER_APPROVAL_MESSAGES.FAILED_TO_LOAD}
						type='error'
						closable
						style={{ marginBottom: 16 }}
					/>
				)}
				<Alert
					message={RIDER_APPROVAL_MESSAGES.CURRENT_APPROVAL_FLOW}
					description={getApprovalFlowDescription()}
					type={getApprovalFlowType()}
					showIcon
					style={{ marginBottom: 24 }}
				/>
				{/* Corporate Selection */}
				<Row gutter={[24, 16]} style={{ marginBottom: 24 }}>
					<Col xs={24} sm={12} md={8}>
						<label
							style={{ display: "block", marginBottom: 8, fontWeight: "bold" }}
						>
							Corporate:
						</label>
						<Select
							style={{ width: "100%" }}
							placeholder={
								hasNoCorporates
									? "No corporates available"
									: "Choose a corporate"
							}
							value={selectedCorporateId}
							onChange={handleCorporateChange}
							allowClear
							disabled={hasNoCorporates}
							loading={corporates?.loading}
						>
							{userCorporates.map((corp) => (
								<Option key={corp.id} value={corp.id}>
									{corp.name}
								</Option>
							))}
						</Select>

						<div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
							{corporateHelperText}
						</div>
					</Col>
				</Row>

				{/* Form – only when a corporate is selected */}
				{selectedCorporateId && !hasNoCorporates && (
					<Form form={form} layout="vertical" onValuesChange={handleFormChange}>
						<Row gutter={[24, 16]}>
							<Col xs={24} sm={12} md={6}>
								<Form.Item
									name="riderApprovalEnabled"
									label={RIDER_APPROVAL_MESSAGES.DRIVER_APPROVAL_FEATURE_LABEL}
									valuePropName="checked"
									tooltip={RIDER_APPROVAL_MESSAGES.DRIVER_APPROVAL_FEATURE_TOOLTIP}
								>
									<Switch
										checkedChildren={RIDER_APPROVAL_MESSAGES.ENABLED}
										unCheckedChildren={RIDER_APPROVAL_MESSAGES.DISABLED}
										disabled
										checked
									/>
								</Form.Item>
							</Col>

							<Col xs={24} sm={12} md={6}>
								<Form.Item
									name="approvalRequired"
									label={RIDER_APPROVAL_MESSAGES.MANUAL_APPROVAL_LABEL}
									valuePropName="checked"
									tooltip={RIDER_APPROVAL_MESSAGES.MANUAL_APPROVAL_TOOLTIP}
								>
									<Switch
										checkedChildren={RIDER_APPROVAL_MESSAGES.REQUIRED}
										unCheckedChildren={RIDER_APPROVAL_MESSAGES.NOT_REQUIRED}
									/>
								</Form.Item>
							</Col>

							<Col xs={24} sm={12} md={6}>
								<Form.Item
									name="autoApprovalEnabled"
									label={RIDER_APPROVAL_MESSAGES.AUTO_APPROVAL_LABEL}
									valuePropName="checked"
									tooltip={RIDER_APPROVAL_MESSAGES.AUTO_APPROVAL_TOOLTIP}
								>
									<Switch
										checkedChildren={RIDER_APPROVAL_MESSAGES.ENABLED}
										unCheckedChildren={RIDER_APPROVAL_MESSAGES.DISABLED}
										disabled={formValues.approvalRequired}
									/>
								</Form.Item>
							</Col>

							<Col xs={24} sm={12} md={6}>
								<Form.Item
									name="autoApprovalAction"
									label={RIDER_APPROVAL_MESSAGES.AUTO_APPROVAL_ACTION_LABEL}
									tooltip={RIDER_APPROVAL_MESSAGES.AUTO_APPROVAL_ACTION_TOOLTIP}
								>
									<Select
										disabled={formValues.approvalRequired}
										style={
											formValues.approvalRequired
												? { opacity: 0.5, backgroundColor: "#f5f5f5" }
												: {}
										}
									>
										<Option value="APPROVED">
											{RIDER_APPROVAL_MESSAGES.AUTO_APPROVE}
										</Option>
										<Option value="DENIED">
											{RIDER_APPROVAL_MESSAGES.AUTO_DENY}
										</Option>
									</Select>
								</Form.Item>
							</Col>
						</Row>
					</Form>
				)}
				<Divider />

				<Row gutter={16}>
					<Col span={24}>
						<h4>{RIDER_APPROVAL_MESSAGES.GUIDELINES_TITLE}</h4>
						<ul style={{ paddingLeft: 20, color: "#666" }}>
							<li>
								<strong>
									{RIDER_APPROVAL_MESSAGES.DRIVER_APPROVAL_FEATURE_LABEL}:
								</strong>{" "}
								{RIDER_APPROVAL_MESSAGES.GUIDELINE_DRIVER_APPROVAL}
							</li>
							<li>
								<strong>{RIDER_APPROVAL_MESSAGES.MANUAL_APPROVAL_LABEL}:</strong>{" "}
								{RIDER_APPROVAL_MESSAGES.GUIDELINE_MANUAL_APPROVAL}
							</li>
							<li>
								<strong>{RIDER_APPROVAL_MESSAGES.AUTO_APPROVAL_LABEL}:</strong>{" "}
								{RIDER_APPROVAL_MESSAGES.GUIDELINE_AUTO_APPROVAL}
							</li>
							<li>
								<strong>
									{RIDER_APPROVAL_MESSAGES.AUTO_APPROVAL_ACTION_LABEL}:
								</strong>{" "}
								{RIDER_APPROVAL_MESSAGES.GUIDELINE_AUTO_APPROVAL_ACTION}
							</li>
						</ul>
					</Col>
				</Row>
			</Card>
		</Spin>
	);
};

export default RiderApprovalSettings;
